import React, { useState } from 'react';
import { Container, Row, Col, Card, Nav, Tab } from 'react-bootstrap';
import { useQuery } from 'react-query';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getConnections, getPendingRequests, ReceivedPendingRequest, SentPendingRequest, User } from '../services/userService';

// Import sub-components
import ConnectionList from '../components/connections/ConnectionList';
import RequestList from '../components/connections/RequestList';

// Define the structure for the pending requests query data
interface PendingRequestsData {
    received: ReceivedPendingRequest[];
    sent: SentPendingRequest[];
}

const ConnectionsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState('connections');

    // Fetch connections
    const { 
        data: connectionsData, 
        isLoading: isLoadingConnections,
        error: connectionsError 
    } = useQuery<User[], Error>('connections', () => getConnections(), {
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Fetch pending requests
    const { 
        data: pendingRequestsData, 
        isLoading: isLoadingRequests,
        error: requestsError 
    } = useQuery<PendingRequestsData, Error>(
        'pendingRequests', 
        getPendingRequests, 
        {
            staleTime: 0, // Keep this set to 0 as it fixed the issue
            initialData: { received: [], sent: [] } 
        }
    );

    // Use optional chaining and nullish coalescing for safety
    const connectionsCount = connectionsData?.length ?? 0;
    // Use optional chaining here as well
    const receivedRequestsCount = pendingRequestsData?.received?.length ?? 0;
    const sentRequestsCount = pendingRequestsData?.sent?.length ?? 0;

    return (
        <Container className="py-5">
            <Row>
                {/* Sidebar */}
                <Col lg={3}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <h5 className="fw-bold mb-3">Network</h5>
                            <Nav variant="pills" className="flex-column" activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'connections')}>
                                <Nav.Item className="mb-2">
                                    <Nav.Link eventKey="connections" className="w-100 text-start rounded-pill d-flex justify-content-between align-items-center">
                                        <span><i className="fas fa-user-friends me-2"></i> Connections</span>
                                        <span className="badge bg-primary rounded-pill">{connectionsCount}</span>
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item className="mb-2">
                                    <Nav.Link eventKey="requests" className="w-100 text-start rounded-pill d-flex justify-content-between align-items-center">
                                        <span><i className="fas fa-user-plus me-2"></i> Requests</span>
                                        <span className="badge bg-primary rounded-pill">{receivedRequestsCount}</span>
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="sent" className="w-100 text-start rounded-pill d-flex justify-content-between align-items-center">
                                        <span><i className="fas fa-paper-plane me-2"></i> Sent</span>
                                        <span className="badge bg-primary rounded-pill">{sentRequestsCount}</span>
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Card.Body>
                    </Card>
                    {/* Add Suggestions Card here later if needed */}
                </Col>

                {/* Main Content Area */}
                <Col lg={9}>
                    <Tab.Container activeKey={activeTab}>
                        <Tab.Content>
                            <Tab.Pane eventKey="connections">
                                <Card className="border-0 shadow-sm">
                                    <Card.Body>
                                        <h4 className="fw-bold mb-4">Your Connections ({connectionsCount})</h4>
                                        {isLoadingConnections ? (
                                            <div className="text-center py-5"><LoadingSpinner text="Loading connections..." /></div>
                                        ) : connectionsError ? (
                                            <div className="alert alert-danger">Error loading connections: {connectionsError.message}</div>
                                        ) : (
                                            <ConnectionList connections={connectionsData || []} />
                                        )}
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>
                            <Tab.Pane eventKey="requests">
                                <Card className="border-0 shadow-sm">
                                    <Card.Body>
                                        <h4 className="fw-bold mb-4">Received Requests ({receivedRequestsCount})</h4>
                                        {isLoadingRequests ? (
                                            <div className="text-center py-5"><LoadingSpinner text="Loading requests..." /></div>
                                        ) : requestsError ? (
                                            <div className="alert alert-danger">Error loading requests: {requestsError.message}</div>
                                        ) : (
                                            <RequestList requests={pendingRequestsData?.received || []} type="received" />
                                        )}
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>
                            <Tab.Pane eventKey="sent">
                                <Card className="border-0 shadow-sm">
                                    <Card.Body>
                                        <h4 className="fw-bold mb-4">Sent Requests ({sentRequestsCount})</h4>
                                        {isLoadingRequests ? (
                                            <div className="text-center py-5"><LoadingSpinner text="Loading requests..." /></div>
                                        ) : requestsError ? (
                                            <div className="alert alert-danger">Error loading requests: {requestsError.message}</div>
                                        ) : (
                                            <RequestList requests={pendingRequestsData?.sent || []} type="sent" />
                                        )}
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Col>
            </Row>
        </Container>
    );
};

export default ConnectionsPage;
